import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams, useNavigate } from 'react-router-dom';
import useToggleSidebar from "../../../components/togglesidebar";
import Sidebar from '../../sidebar';
import Header from '../../Header';
import Footer from '../../footer';
import axios from "axios";
import API_URL, { buildImageUrl } from "../../../config/config";
import Modal from '../../../components/Modal/Modal';
import '../../../styles/challengerequests.css';

const isValidObjectId = (v) => typeof v === "string" && /^[a-fA-F0-9]{24}$/.test(v.trim());

function EditProvider() {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("adminToken");
    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

    const formatFileSize = (bytes) => {
        const n = Number(bytes) || 0;
        if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}MB`;
        return `${Math.ceil(n / 1024)}KB`;
    };

    const [formData, setFormData] = useState({
        name: "",
        url: "",
        website: "",
        metaKeywords: "",
        address: "",
        postalCode: "",
        numberOfEmployees: "",
        foundedYear: "",
        shortDescription: "",
        contactNumber: "",
        coverImage: "",
        coverThumb: "",
        icon: "",
        iconThumb: "",
        rating: 0,
        priceTier: "$$",
        isVerified: false,
        isActive: true,
        location: "",
        highlights: [],
        features: [],
        fabrics: [],
        categories: [],
        topSellers: [],
        socialMedia: [],
        shipping: {
            info: "",
            cost: "",
            deliveryTime: ""
        }
    });

    const [imagePreviews, setImagePreviews] = useState({
        coverImageUrl: "",
        iconUrl: ""
    });

    const [selectedUploadInfo, setSelectedUploadInfo] = useState({ coverImage: null, icon: null });
    const [imageUploadErrors, setImageUploadErrors] = useState({ coverImage: null, icon: null });

    const [uploading, setUploading] = useState({
        coverImage: false,
        icon: false
    });

    const [dragging, setDragging] = useState({
        coverImage: false,
        icon: false
    });

    const [hoveredUpload, setHoveredUpload] = useState(null);

    const [options, setOptions] = useState({
        countries: [],
        highlights: [],
        features: [],
        fabrics: [],
        categories: []
    });

    const [backendMessage, setBackendMessage] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Fetch provider data and options
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Get base API URL
                const baseApiUrl = API_URL.replace(/\/admin$/, '');

                // Fetch provider details
                const providerResponse = await axios.get(`${API_URL}/providers/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (providerResponse.data.success && providerResponse.data.item) {
                    const provider = providerResponse.data.item;
                    setFormData({
                        name: provider.name || "",
                        url: provider.url || "",
                        website: provider.website || "",
                        metaKeywords: provider.metaKeywords || "",
                        address: provider.address || "",
                        postalCode: provider.postalCode || "",
                        numberOfEmployees: provider.numberOfEmployees || "",
                        foundedYear: provider.foundedYear || "",
                        shortDescription: provider.shortDescription || "",
                        contactNumber: provider.contactNumber || "",
                        coverImage: provider.coverImage || "",
                        coverThumb: provider.coverThumb || "",
                        icon: provider.icon || "",
                        iconThumb: provider.iconThumb || "",
                        rating: provider.rating || 0,
                        priceTier: provider.priceTier || "$$",
                        isVerified: provider.isVerified || false,
                        isActive: provider.isActive !== undefined ? provider.isActive : true,
                        location: typeof provider.location === 'object' ? (provider.location._id || provider.location) : provider.location || "",
                        highlights: provider.highlights ? provider.highlights.map(h => typeof h === 'object' ? h._id || h : h) : [],
                        features: provider.features ? provider.features.map(f => typeof f === 'object' ? f._id || f : f) : [],
                        fabrics: provider.fabrics ? provider.fabrics.map(f => typeof f === 'object' ? f._id || f : f) : [],
                        categories: provider.categories ? provider.categories.map(c => typeof c === 'object' ? c._id || c : c) : [],
                        topSellers: provider.topSellers || [],
                        socialMedia: provider.socialMedia || [],
                        shipping: provider.shipping || { info: "", cost: "", deliveryTime: "" }
                    });

                    setImagePreviews({
                        coverImageUrl: buildImageUrl(provider.coverImage || ""),
                        iconUrl: buildImageUrl(provider.icon || "")
                    });
                }

                // Fetch options in parallel (baseApiUrl already includes /api, so don't add it again)
                const [countriesRes, highlightsRes, featuresRes, fabricsRes, categoriesRes] = await Promise.all([
                    axios.get(`${baseApiUrl}/countries`).catch(() => ({ data: { countries: [] } })),
                    axios.get(`${baseApiUrl}/highlights`).catch(() => ({ data: { highlights: [] } })),
                    axios.get(`${baseApiUrl}/features`).catch(() => ({ data: { features: [] } })),
                    axios.get(`${baseApiUrl}/fabrics`).catch(() => ({ data: { fabrics: [] } })),
                    axios.get(`${baseApiUrl}/categories`).catch(() => ({ data: { categories: [] } }))
                ]);

                // Helper function to safely extract array from response
                // Handles different backend response structures:
                // - { success: true, data: [...] } (highlights)
                // - { success: true, features: [...] } (features)
                // - { success: true, fabrics: [...] } (fabrics)
                // - { success: true, categories: [...], data: [...] } (categories)
                const extractArray = (response, key) => {
                    if (!response || !response.data) return [];
                    const data = response.data;
                    // If data is already an array, return it
                    if (Array.isArray(data)) return data;
                    // Try the specific key (e.g., 'features', 'fabrics', 'categories')
                    if (Array.isArray(data[key])) return data[key];
                    // Try 'data' property (e.g., highlights returns { success: true, data: [...] })
                    if (Array.isArray(data.data)) return data.data;
                    // Fallback to empty array
                    return [];
                };

                setOptions({
                    countries: extractArray(countriesRes, 'countries'),
                    highlights: extractArray(highlightsRes, 'highlights'),
                    features: extractArray(featuresRes, 'features'),
                    fabrics: extractArray(fabricsRes, 'fabrics'),
                    categories: extractArray(categoriesRes, 'categories')
                });
            } catch (err) {
                console.error("Error loading provider:", err);
                setBackendMessage({ type: 'danger', text: 'Failed to load provider for editing.' });
            } finally {
                setLoading(false);
            }
        };

        if (id && token) {
            fetchData();
        }
    }, [id, token]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));

        if (name === "coverImage") {
            setImagePreviews(prev => ({ ...prev, coverImageUrl: buildImageUrl(value) }));
        }
        if (name === "icon") {
            setImagePreviews(prev => ({ ...prev, iconUrl: buildImageUrl(value) }));
        }
    };

    const triggerFileInput = (inputId) => {
        const el = document.getElementById(inputId);
        if (el) el.click();
    };

    const handleFileChange = async (field, file) => {
        if (!file) return;

        setImageUploadErrors((prev) => ({ ...prev, [field]: null }));

        setSelectedUploadInfo((prev) => ({
            ...prev,
            [field]: { name: file.name, size: formatFileSize(file.size) }
        }));

        const previewUrl = URL.createObjectURL(file);
        const previousValue = formData[field];

        setImagePreviews(prev => ({
            ...prev,
            [field === 'coverImage' ? 'coverImageUrl' : 'iconUrl']: previewUrl
        }));
        setUploading(prev => ({ ...prev, [field]: true }));

        try {
            const form = new FormData();
            form.append(field, file);
            const baseApi = API_URL.replace(/\/admin$/, '');
            const res = await axios.post(`${baseApi}/providers/upload-image`, form, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            const uploadedValue = (res.data && (res.data[field] || res.data.fileUrl || res.data.url || res.data.path))
                ? buildImageUrl(res.data[field] || res.data.fileUrl || res.data.url || res.data.path)
                : '';

            if (!uploadedValue) {
                throw new Error('Invalid upload response');
            }

            // Update both image and thumbnail fields based on response
            if (field === 'coverImage') {
                setFormData(prev => ({ 
                    ...prev, 
                    coverImage: uploadedValue,
                    coverThumb: res.data?.thumbCoverImage || uploadedValue.replace(/\/originals\//g, '/thumbs/')
                }));
            } else if (field === 'icon') {
                setFormData(prev => ({ 
                    ...prev, 
                    icon: uploadedValue,
                    iconThumb: res.data?.thumbIcon || uploadedValue.replace(/\/originals\//g, '/thumbs/')
                }));
            } else {
                setFormData(prev => ({ ...prev, [field]: uploadedValue }));
            }

            setImagePreviews(prev => ({
                ...prev,
                [field === 'coverImage' ? 'coverImageUrl' : 'iconUrl']: uploadedValue
            }));

            setImageUploadErrors((prev) => ({ ...prev, [field]: null }));
        } catch (err) {
            console.error('Upload failed', err);
            const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
            const msg = serverMsg || 'Image upload failed. Please try again.';
            setBackendMessage({ type: 'danger', text: msg });
            setImageUploadErrors((prev) => ({ ...prev, [field]: msg }));
            setImagePreviews(prev => ({
                ...prev,
                [field === 'coverImage' ? 'coverImageUrl' : 'iconUrl']: buildImageUrl(previousValue)
            }));
        } finally {
            setUploading(prev => ({ ...prev, [field]: false }));
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (field) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(prev => ({ ...prev, [field]: true }));
    };

    const handleDragLeave = (field) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(prev => ({ ...prev, [field]: false }));
    };

    const handleDrop = (field) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(prev => ({ ...prev, [field]: false }));
        const file = e.dataTransfer?.files?.[0];
        if (file) handleFileChange(field, file);
    };

    const handleArrayChange = (field, value, checked) => {
        setFormData(prev => ({
            ...prev,
            [field]: checked
                ? [...prev[field], value]
                : prev[field].filter(item => item !== value)
        }));
    };

    const handleSocialMediaChange = (index, field, value) => {
        setFormData(prev => {
            const newSocialMedia = [...prev.socialMedia];
            if (!newSocialMedia[index]) {
                newSocialMedia[index] = { platform: "", url: "" };
            }
            newSocialMedia[index][field] = value;
            return { ...prev, socialMedia: newSocialMedia };
        });
    };

    const addSocialMediaField = () => {
        setFormData(prev => ({
            ...prev,
            socialMedia: [...prev.socialMedia, { platform: "", url: "" }]
        }));
    };

    const removeSocialMediaField = (index) => {
        setFormData(prev => ({
            ...prev,
            socialMedia: prev.socialMedia.filter((_, i) => i !== index)
        }));
    };

    const handleShippingChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            shipping: {
                ...prev.shipping,
                [field]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setBackendMessage({ type: '', text: '' });

        if (imageUploadErrors.coverImage || imageUploadErrors.icon) {
            const parts = [];
            if (imageUploadErrors.coverImage) parts.push(`Cover image: ${imageUploadErrors.coverImage}`);
            if (imageUploadErrors.icon) parts.push(`Icon: ${imageUploadErrors.icon}`);
            setBackendMessage({ type: 'danger', text: `Please fix image upload errors before updating. ${parts.join(' | ')}` });
            return;
        }

        const errors = [];
        const name = (formData.name || "").trim();
        const url = (formData.url || "").trim();
        if (!name) errors.push("Name is required");
        if (!url) errors.push("URL is required");

        const ratingNum = formData.rating === "" ? 0 : Number(formData.rating);
        if (!Number.isFinite(ratingNum) || ratingNum < 0 || ratingNum > 5) {
            errors.push("Rating must be between 0 and 5");
        }

        if (errors.length) {
            setBackendMessage({ type: 'danger', text: errors.join(". ") });
            return;
        }

        setSubmitting(true);

        try {
            const updateData = { ...formData };

            updateData.name = name;
            updateData.url = url;

            // Convert empty strings to null for number fields
            if (updateData.numberOfEmployees === "") updateData.numberOfEmployees = null;
            if (updateData.foundedYear === "") updateData.foundedYear = null;
            updateData.rating = ratingNum;

            // Normalize location
            if (updateData.location === "") updateData.location = null;

            // Normalize array refs to valid ObjectIds only
            ["highlights", "features", "fabrics", "categories"].forEach((k) => {
                const arr = updateData[k];
                if (!Array.isArray(arr)) {
                    updateData[k] = [];
                    return;
                }
                updateData[k] = arr.map(String).map((s) => s.trim()).filter(isValidObjectId);
            });

            // Normalize social media list (keep only valid rows)
            if (Array.isArray(updateData.socialMedia)) {
                updateData.socialMedia = updateData.socialMedia
                    .map((x) => {
                        const platform = (x?.platform || "").toString().trim();
                        const url = (x?.url || "").toString().trim();
                        return platform && url ? { platform, url } : null;
                    })
                    .filter(Boolean);
            }

            // Normalize top sellers to expected schema
            if (Array.isArray(updateData.topSellers)) {
                updateData.topSellers = updateData.topSellers
                    .map((x) => {
                        const name = (x?.name || "").toString().trim();
                        const link = (x?.link || "").toString().trim();
                        return name ? (link ? { name, link } : { name }) : null;
                    })
                    .filter(Boolean);
            }

            const response = await axios.patch(`${API_URL}/providers/${id}`, updateData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setBackendMessage({ type: 'success', text: 'Provider updated successfully!' });
                setTimeout(() => navigate('/admin/providers'), 1500);
            }
        } catch (err) {
            console.error("Update error:", err);
            setBackendMessage({ 
                type: 'danger', 
                text: err.response?.data?.message || 'Update failed. Try again later.' 
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        setShowDeleteModal(false);
        try {
            const res = await axios.delete(`${API_URL}/providers/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log("Delete success:", res.data);
            setBackendMessage({ type: 'success', text: 'Provider soft deleted successfully. It will be hidden from admin panel but remain visible to users.' });
            setTimeout(() => {
                navigate("/admin/providers");
            }, 2000);
        } catch (err) {
            console.error("Delete failed:", err.response?.data || err.message);
            setBackendMessage({ type: 'danger', text: err.response?.data?.message || "Failed to delete provider." });
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) return (
        <div className="content-wrapper" style={{ marginLeft: "70px", transition: "margin-left .3s" }}>
            <div className="text-center py-5">Loading provider data...</div>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>Admin | Edit Provider</title>
            </Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <section className='content'>
                        <div className="breadcrumb-main-bx">
                            <div className="breadcrumb-bx">
                                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <Link className="breadcrumb-link" to="/admin/providers">Providers</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <div className="breadcrumb-link breadcrumb-active">Edit Provider</div>
                            </div>
                        </div>

                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <div>Edit Provider</div>
                                            <button 
                                                className="btn btn-danger btn-sm" 
                                                onClick={() => setShowDeleteModal(true)}
                                                disabled={isDeleting}
                                            >
                                                Delete Provider
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {backendMessage.text && (
                                                <div className={`alert alert-${backendMessage.type === 'success' ? 'success' : 'danger'}`} role="alert">
                                                    {backendMessage.text}
                                                </div>
                                            )}

                                            <form onSubmit={handleSubmit}>
                                                <div className="row">
                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="name">Name *</label>
                                                        <input type="text" className="form-control" id="name" name="name" value={formData.name} onChange={handleChange} required />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="url">URL *</label>
                                                        <input type="text" className="form-control" id="url" name="url" value={formData.url} onChange={handleChange} required />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="website">Website</label>
                                                        <input type="text" className="form-control" id="website" name="website" value={formData.website} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="location">Location</label>
                                                        <select className="form-control" id="location" name="location" value={formData.location} onChange={handleChange}>
                                                            <option value="">Select Country</option>
                                                            {(Array.isArray(options.countries) ? options.countries : []).map(country => (
                                                                <option key={country._id || country.id} value={country._id || country.id}>
                                                                    {country.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="address">Address</label>
                                                        <input type="text" className="form-control" id="address" name="address" value={formData.address} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="postalCode">Postal Code</label>
                                                        <input type="text" className="form-control" id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="contactNumber">Contact Number</label>
                                                        <input type="text" className="form-control" id="contactNumber" name="contactNumber" value={formData.contactNumber} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="numberOfEmployees">Number of Employees</label>
                                                        <input type="number" className="form-control" id="numberOfEmployees" name="numberOfEmployees" value={formData.numberOfEmployees} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="foundedYear">Founded Year</label>
                                                        <input type="number" className="form-control" id="foundedYear" name="foundedYear" value={formData.foundedYear} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="rating">Rating</label>
                                                        <input type="number" step="0.1" className="form-control" id="rating" name="rating" value={formData.rating} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="priceTier">Price Tier</label>
                                                        <select className="form-control" id="priceTier" name="priceTier" value={formData.priceTier} onChange={handleChange}>
                                                            <option value="$">$</option>
                                                            <option value="$$">$$</option>
                                                            <option value="$$$">$$$</option>
                                                            <option value="$$$$">$$$$</option>
                                                        </select>
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label htmlFor="shortDescription">Short Description</label>
                                                        <textarea className="form-control" id="shortDescription" name="shortDescription" rows="4" value={formData.shortDescription} onChange={handleChange} />
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label htmlFor="metaKeywords">Meta Keywords (comma separated)</label>
                                                        <input type="text" className="form-control" id="metaKeywords" name="metaKeywords" value={formData.metaKeywords} onChange={handleChange} placeholder="medical scrubs, uniforms, custom apparel" />
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="coverImage">Cover Image URL</label>
                                                        <input type="text" className="form-control" id="coverImage" name="coverImage" value={formData.coverImage} onChange={handleChange} />

                                                        <div
                                                            className="mt-2 position-relative"
                                                            style={{
                                                                border: `2px dashed ${dragging.coverImage ? '#0d6efd' : '#ced4da'}`,
                                                                borderRadius: 8,
                                                                padding: imagePreviews.coverImageUrl ? 8 : 20,
                                                                cursor: 'pointer',
                                                                minHeight: 170
                                                            }}
                                                            onClick={() => triggerFileInput('cover-image-file')}
                                                            onDragOver={handleDragOver}
                                                            onDragEnter={handleDragEnter('coverImage')}
                                                            onDragLeave={handleDragLeave('coverImage')}
                                                            onDrop={handleDrop('coverImage')}
                                                            onMouseEnter={() => setHoveredUpload('coverImage')}
                                                            onMouseLeave={() => setHoveredUpload(null)}
                                                        >
                                                            {imagePreviews.coverImageUrl ? (
                                                                <>
                                                                    <img
                                                                        src={imagePreviews.coverImageUrl}
                                                                        alt="Cover Preview"
                                                                        style={{
                                                                            width: '100%',
                                                                            maxHeight: 160,
                                                                            objectFit: 'contain',
                                                                            display: 'block',
                                                                            borderRadius: 6
                                                                        }}
                                                                    />
                                                                    {hoveredUpload === 'coverImage' && !uploading.coverImage && (
                                                                        <div
                                                                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                background: 'rgba(255,255,255,0.55)',
                                                                                borderRadius: 8
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    background: 'rgba(255,255,255,0.95)',
                                                                                    padding: '6px 12px',
                                                                                    borderRadius: 6,
                                                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                                                                                    fontSize: 13,
                                                                                    fontWeight: 600
                                                                                }}
                                                                            >
                                                                                Click to change image
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="text-center" style={{ color: '#6c757d' }}>
                                                                    <div style={{ fontWeight: 600 }}>Click to upload or drag and drop</div>
                                                                    <div style={{ fontSize: 12 }}>PNG, JPG up to 500KB</div>
                                                                </div>
                                                            )}

                                                            {uploading.coverImage && (
                                                                <div
                                                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        background: 'rgba(255,255,255,0.7)',
                                                                        borderRadius: 8
                                                                    }}
                                                                >
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <div className="spinner-border text-primary spinner-border-sm" role="status" />
                                                                        <div style={{ fontWeight: 600 }}>Uploading...</div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <input
                                                                id="cover-image-file"
                                                                type="file"
                                                                accept="image/*"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => {
                                                                    const f = e.target.files?.[0];
                                                                    handleFileChange('coverImage', f);
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="mt-1" style={{ fontSize: 12, color: '#6c757d' }}>
                                                            Max 500KB{selectedUploadInfo.coverImage ? ` | Selected: ${selectedUploadInfo.coverImage.size}` : ''}
                                                        </div>
                                                        {imageUploadErrors.coverImage && (
                                                            <div className="mt-1" style={{ fontSize: 12, color: '#dc3545' }}>
                                                                {imageUploadErrors.coverImage}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <label htmlFor="icon">Icon URL</label>
                                                        <input type="text" className="form-control" id="icon" name="icon" value={formData.icon} onChange={handleChange} />

                                                        <div
                                                            className="mt-2 position-relative"
                                                            style={{
                                                                border: `2px dashed ${dragging.icon ? '#0d6efd' : '#ced4da'}`,
                                                                borderRadius: 8,
                                                                padding: imagePreviews.iconUrl ? 8 : 20,
                                                                cursor: 'pointer',
                                                                minHeight: 170,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                            onClick={() => triggerFileInput('icon-file')}
                                                            onDragOver={handleDragOver}
                                                            onDragEnter={handleDragEnter('icon')}
                                                            onDragLeave={handleDragLeave('icon')}
                                                            onDrop={handleDrop('icon')}
                                                            onMouseEnter={() => setHoveredUpload('icon')}
                                                            onMouseLeave={() => setHoveredUpload(null)}
                                                        >
                                                            {imagePreviews.iconUrl ? (
                                                                <>
                                                                    <img
                                                                        src={imagePreviews.iconUrl}
                                                                        alt="Icon Preview"
                                                                        style={{
                                                                            width: 120,
                                                                            height: 120,
                                                                            objectFit: 'contain',
                                                                            display: 'block',
                                                                            borderRadius: 6
                                                                        }}
                                                                    />
                                                                    {hoveredUpload === 'icon' && !uploading.icon && (
                                                                        <div
                                                                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                background: 'rgba(255,255,255,0.55)',
                                                                                borderRadius: 8
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    background: 'rgba(255,255,255,0.95)',
                                                                                    padding: '6px 12px',
                                                                                    borderRadius: 6,
                                                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                                                                                    fontSize: 13,
                                                                                    fontWeight: 600
                                                                                }}
                                                                            >
                                                                                Click to change image
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="text-center" style={{ color: '#6c757d' }}>
                                                                    <div style={{ fontWeight: 600 }}>Click to upload or drag and drop</div>
                                                                    <div style={{ fontSize: 12 }}>PNG, JPG up to 500KB</div>
                                                                </div>
                                                            )}

                                                            {uploading.icon && (
                                                                <div
                                                                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        background: 'rgba(255,255,255,0.7)',
                                                                        borderRadius: 8
                                                                    }}
                                                                >
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <div className="spinner-border text-primary spinner-border-sm" role="status" />
                                                                        <div style={{ fontWeight: 600 }}>Uploading...</div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <input
                                                                id="icon-file"
                                                                type="file"
                                                                accept="image/*"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => {
                                                                    const f = e.target.files?.[0];
                                                                    handleFileChange('icon', f);
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="mt-1" style={{ fontSize: 12, color: '#6c757d' }}>
                                                            Max 500KB{selectedUploadInfo.icon ? ` | Selected: ${selectedUploadInfo.icon.size}` : ''}
                                                        </div>
                                                        {imageUploadErrors.icon && (
                                                            <div className="mt-1" style={{ fontSize: 12, color: '#dc3545' }}>
                                                                {imageUploadErrors.icon}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <div className="form-check">
                                                            <input className="form-check-input" type="checkbox" id="isVerified" name="isVerified" checked={formData.isVerified} onChange={handleChange} />
                                                            <label className="form-check-label" htmlFor="isVerified">Is Verified</label>
                                                        </div>
                                                    </div>

                                                    <div className="form-group col-sm-6">
                                                        <div className="form-check">
                                                            <input className="form-check-input" type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={handleChange} />
                                                            <label className="form-check-label" htmlFor="isActive">Is Active</label>
                                                        </div>
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label>Highlights</label>
                                                        <div className="row">
                                                            {(Array.isArray(options.highlights) ? options.highlights : []).map(highlight => (
                                                                <div key={highlight._id || highlight.id} className="col-sm-6 col-md-4">
                                                                    <div className="form-check">
                                                                        <input 
                                                                            className="form-check-input" 
                                                                            type="checkbox" 
                                                                            id={`highlight-${highlight._id || highlight.id}`}
                                                                            checked={formData.highlights.includes(highlight._id || highlight.id)}
                                                                            onChange={(e) => handleArrayChange('highlights', highlight._id || highlight.id, e.target.checked)}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`highlight-${highlight._id || highlight.id}`}>
                                                                            {highlight.name}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label>Features</label>
                                                        <div className="row">
                                                            {(Array.isArray(options.features) ? options.features : []).map(feature => (
                                                                <div key={feature._id || feature.id} className="col-sm-6 col-md-4">
                                                                    <div className="form-check">
                                                                        <input 
                                                                            className="form-check-input" 
                                                                            type="checkbox" 
                                                                            id={`feature-${feature._id || feature.id}`}
                                                                            checked={formData.features.includes(feature._id || feature.id)}
                                                                            onChange={(e) => handleArrayChange('features', feature._id || feature.id, e.target.checked)}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`feature-${feature._id || feature.id}`}>
                                                                            {feature.name}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label>Fabrics</label>
                                                        <div className="row">
                                                            {(Array.isArray(options.fabrics) ? options.fabrics : []).map(fabric => (
                                                                <div key={fabric._id || fabric.id} className="col-sm-6 col-md-4">
                                                                    <div className="form-check">
                                                                        <input 
                                                                            className="form-check-input" 
                                                                            type="checkbox" 
                                                                            id={`fabric-${fabric._id || fabric.id}`}
                                                                            checked={formData.fabrics.includes(fabric._id || fabric.id)}
                                                                            onChange={(e) => handleArrayChange('fabrics', fabric._id || fabric.id, e.target.checked)}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`fabric-${fabric._id || fabric.id}`}>
                                                                            {fabric.name}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label>Categories</label>
                                                        <div className="row">
                                                            {(Array.isArray(options.categories) ? options.categories : []).map(category => (
                                                                <div key={category._id || category.id} className="col-sm-6 col-md-4">
                                                                    <div className="form-check">
                                                                        <input 
                                                                            className="form-check-input" 
                                                                            type="checkbox" 
                                                                            id={`category-${category._id || category.id}`}
                                                                            checked={formData.categories.includes(category._id || category.id)}
                                                                            onChange={(e) => handleArrayChange('categories', category._id || category.id, e.target.checked)}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`category-${category._id || category.id}`}>
                                                                            {category.name}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="form-group col-sm-12">
                                                        <label>Social Media</label>
                                                        {formData.socialMedia.map((social, index) => (
                                                            <div key={index} className="row mb-2">
                                                                <div className="col-sm-5">
                                                                    <input 
                                                                        type="text" 
                                                                        className="form-control" 
                                                                        placeholder="Platform (e.g., Twitter)"
                                                                        value={social.platform} 
                                                                        onChange={(e) => handleSocialMediaChange(index, 'platform', e.target.value)} 
                                                                    />
                                                                </div>
                                                                <div className="col-sm-6">
                                                                    <input 
                                                                        type="text" 
                                                                        className="form-control" 
                                                                        placeholder="URL"
                                                                        value={social.url} 
                                                                        onChange={(e) => handleSocialMediaChange(index, 'url', e.target.value)} 
                                                                    />
                                                                </div>
                                                                <div className="col-sm-1">
                                                                    <button type="button" className="btn btn-danger btn-sm" onClick={() => removeSocialMediaField(index)}>Remove</button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <button type="button" className="btn btn-secondary btn-sm" onClick={addSocialMediaField}>Add Social Media</button>
                                                    </div>

                                                    <div className="form-group col-sm-4">
                                                        <label htmlFor="shippingInfo">Shipping Info</label>
                                                        <input type="text" className="form-control" id="shippingInfo" value={formData.shipping.info} onChange={(e) => handleShippingChange('info', e.target.value)} />
                                                    </div>

                                                    <div className="form-group col-sm-4">
                                                        <label htmlFor="shippingCost">Shipping Cost</label>
                                                        <input type="text" className="form-control" id="shippingCost" value={formData.shipping.cost} onChange={(e) => handleShippingChange('cost', e.target.value)} />
                                                    </div>

                                                    <div className="form-group col-sm-4">
                                                        <label htmlFor="deliveryTime">Delivery Time</label>
                                                        <input type="text" className="form-control" id="deliveryTime" value={formData.shipping.deliveryTime} onChange={(e) => handleShippingChange('deliveryTime', e.target.value)} />
                                                    </div>
                                                </div>

                                                <button type="submit" className="btn btn-primary mt-3" disabled={submitting || uploading.coverImage || uploading.icon || !!imageUploadErrors.coverImage || !!imageUploadErrors.icon}>
                                                    {submitting ? 'Updating...' : 'Update Provider'}
                                                </button>
                                                <Link to="/admin/providers" className="btn btn-secondary ms-2 mt-3">Cancel</Link>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>
            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Provider"
                message="Are you sure you want to soft delete this provider? It will be hidden from the admin panel but remain visible to users."
                confirmText="Delete"
                cancelText="Cancel"
            />
        </>
    );
}

export default EditProvider;
